import { useCallback, useEffect, useState } from "react";

import type { CountriesApiResponse, CountryItem, PopupData } from "./types/CountrySelector.types";

import { appendQueryString, getMarketUrlKey } from "./redirectSource";
import { useSearchParamsConfig } from "./useSearchParamsConfig";

/**
 * Hook for filtering list and managing popup display
 */
export const useCountryDisplay = (
  initialList: CountryItem[] = [],
  enabledData: CountriesApiResponse,
  staticMode = false
): {
  filteredList: CountryItem[];
  popupOpen: boolean;
  popupData: PopupData | null;
  closePopup: () => void;
} => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupData, setPopupData] = useState<PopupData | null>(null);
  const [filteredList, setFilteredList] = useState<CountryItem[]>([]);
  const { search: queryString, activeSource, noGeo, deviceCountry, userCountry } = useSearchParamsConfig();

  const isDeviceSource = activeSource?.id === "device";

  const closePopup = useCallback(() => setPopupOpen(false), []);

  useEffect(() => {
    if (staticMode) {
      setFilteredList(initialList);
      return;
    }

    const list = initialList.reduce<CountryItem[]>((acc, item) => {
      const config = enabledData.find((e) => e.code === item.countryCode);
      const urlKey = getMarketUrlKey(activeSource);
      const url = config?.data[urlKey];

      if (url) {
        acc.push({ ...item, url: appendQueryString(url, queryString) });
      }

      return acc;
    }, []);
    setFilteredList(list);
  }, [initialList, enabledData, activeSource, queryString, staticMode]);

  useEffect(() => {
    if (staticMode || !isDeviceSource || noGeo) return;
    if (deviceCountry && userCountry && deviceCountry !== userCountry) {
      const userConfig = enabledData.find((dataItem) => dataItem.code === userCountry);
      const deviceConfig = enabledData.find((dataItem) => dataItem.code === deviceCountry);
      const userFlag = initialList.find((listItem) => listItem.countryCode === userCountry)?.fileReference || "";
      setPopupData({
        popupIpUrl: appendQueryString(userConfig?.data.DeviceRegistrationUrl ?? "", queryString),
        popupPurchaseUrl: appendQueryString(deviceConfig?.data.DeviceRegistrationUrl ?? "", queryString),
        purchaseLocationName: deviceCountry,
        ipLocationName: userCountry,
        flagUrl: userFlag,
      });
      setPopupOpen(true);
    }
  }, [initialList, enabledData, deviceCountry, userCountry, isDeviceSource, noGeo, queryString, staticMode]);

  return { filteredList, popupOpen, popupData, closePopup };
};
